import React from 'react';

/**
 * ExtraIcon — inline line-icons for the Optional Extras spec-card grid.
 *
 * Same visual language as SpecIcon (Trailer Details): decorative, aria-hidden,
 * stroke = currentColor so the card CSS controls the color (blue in the rounded
 * square, white on hover). Shapes are the hand-designed BigLoads Optional Extras
 * icon set (64x64 coordinate space), stripped of their own disc/color so they sit
 * cleanly inside the shared spec-card icon square.
 *
 * Keyed by the category emitted by util/addonCategories.js (categorizeAddon).
 * Falls back to the generic (+ in circle) icon for anything unmapped.
 */

const S = {
  width: 24,
  height: 24,
  viewBox: '0 0 64 64',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 3.2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: 'false',
};

const paths = {
  hitch: <><circle cx="25" cy="22" r="6" /><path d="M25 28v11" /><path d="M19 39h12" /><path d="M31 35h9l5-6" /><path d="M45 29h7v8h-7" /></>,
  strap: <><path d="M20 21c9-6 22-4 24 4 2 9-9 13-18 9-7-3-7-10-1-14 5-3 14-1 15 4" /><path d="M22 37l-5 6" /><path d="M17 43l-4 4" /><path d="M42 36l7 7" /><rect x="43" y="40" width="8" height="6" rx="1" /></>,
  winch: <><rect x="17" y="24" width="30" height="13" rx="3" /><path d="M22 24v13M42 24v13" /><path d="M26 28h12M26 32h12" /><path d="M32 37v7" /><path d="M32 44c0 5 7 6 7 1 0-2-1-3-3-4" /></>,
  delivery: <><path d="M13 34h15v8H13z" /><path d="M28 38h7" /><path d="M35 28h9l7 7v7H35z" /><circle cx="20" cy="44" r="3" /><circle cx="43" cy="44" r="3" /></>,
  electrical: <><circle cx="32" cy="32" r="13" /><circle cx="32" cy="32" r="2" /><circle cx="32" cy="24" r="1.5" /><circle cx="38.5" cy="27.5" r="1.5" /><circle cx="38.5" cy="36.5" r="1.5" /><circle cx="32" cy="40" r="1.5" /><circle cx="25.5" cy="36.5" r="1.5" /><circle cx="25.5" cy="27.5" r="1.5" /></>,
  dolly: <><path d="M28 18v24" /><path d="M28 20h7l3-4" /><path d="M28 42h13" /><path d="M41 42v5" /><circle cx="23" cy="44" r="5" /></>,
  ramp: <><path d="M16 43l13-20h9L25 43z" /><path d="M35 43l10-16h7l-9 16z" /><path d="M22 38h6M26 32h6M30 26h6" /></>,
  chain: <><path d="M25 22l-5 5c-4 4-4 9 0 13s9 4 13 0l4-4" /><path d="M39 42l5-5c4-4 4-9 0-13s-9-4-13 0l-4 4" /><path d="M25 39l14-14" /></>,
  tarp: <><path d="M19 24h26l-4 16H23z" /><path d="M23 40h18l-3 5H26z" /><circle cx="23" cy="28" r="1" /><circle cx="41" cy="28" r="1" /></>,
  tools: <><path d="M20 19l25 25" /><path d="M17 22l5-5 5 5-5 5z" /><path d="M43 20c4 3 5 7 2 10l-4-4-5 5 4 4c-4 3-8 1-10-2" /></>,
  dump: <><path d="M16 34h25l-4 10H20z" /><path d="M18 34l5-10 20 7-2 3" /><path d="M16 44h30" /><circle cx="25" cy="46" r="3" /><circle cx="38" cy="46" r="3" /></>,
  moving: <><rect x="18" y="27" width="13" height="13" rx="1" /><path d="M31 40h12V22h6" /><path d="M43 22v23" /><circle cx="45" cy="47" r="3" /><path d="M15 43h31" /></>,
  lock: <><rect x="20" y="29" width="24" height="18" rx="3" /><path d="M25 29v-5c0-5 3-8 7-8s7 3 7 8v5" /><circle cx="32" cy="38" r="2" /><path d="M32 40v3" /></>,
  insurance: <><path d="M32 17l13 5v9c0 9-6 14-13 17-7-3-13-8-13-17v-9z" /><path d="M26 32l4 4 8-9" /></>,
  generic: <><circle cx="32" cy="32" r="14" /><path d="M32 24v16M24 32h16" /></>,
};

const ExtraIcon = ({ category }) => {
  const key = paths[category] ? category : 'generic';
  return <svg {...S}>{paths[key]}</svg>;
};

export default ExtraIcon;
